import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface FooterData {
  tagline: string;
  copyright: string;
  email: string;
  phone: string;
  address: string;
  instagram: string;
  facebook: string;
  whatsapp: string;
  link1_label: string;
  link1_url: string;
  link2_label: string;
  link2_url: string;
  link3_label: string;
  link3_url: string;
  bg_color: string;
}

const defaults: FooterData = {
  tagline: 'Das destilarias do Mundo, para o litoral sob o Sol',
  copyright: '© 2025 Ma Gádeni. All rights reserved',
  email: 'contato@magadeni.com.br',
  phone: '+55 (81) 99999-9999',
  address: 'Recife, Pernambuco, Brasil',
  instagram: 'https://instagram.com',
  facebook: 'https://facebook.com',
  whatsapp: 'https://wa.me/5581999999999',
  link1_label: 'Fale Conosco',
  link1_url: '/contact',
  link2_label: 'Sobre Nós',
  link2_url: '/our-story',
  link3_label: 'Nossos Whiskies',
  link3_url: '/whiskies',
  bg_color: '#260a23',
};

export default function AdminFooterPage() {
  const [data, setData] = useState<FooterData>(defaults);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadFooter(); }, []);

  const loadFooter = async () => {
    const { data: row } = await supabase
      .from('site_settings')
      .select('key, value, value_json')
      .in('key', ['footer_data']);

    if (row && row.length > 0) {
      const footerRow = row.find(r => r.key === 'footer_data');
      if (footerRow?.value_json) {
        setData({ ...defaults, ...(footerRow.value_json as FooterData) });
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const { data: existing } = await supabase
      .from('site_settings')
      .select('id')
      .eq('key', 'footer_data')
      .maybeSingle();

    if (existing) {
      await supabase.from('site_settings').update({ value_json: data, updated_at: new Date().toISOString() }).eq('key', 'footer_data');
    } else {
      await supabase.from('site_settings').insert({ key: 'footer_data', value_json: data });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors";
  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Gestão do Footer</h1>
          <p className="text-gray-400 text-sm mt-0.5">Edite textos, links e redes sociais do rodapé</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Textos</h2>
            <Field label="Tagline / Frase Principal">
              <textarea className={inputCls} rows={2} value={data.tagline} onChange={e => setData({ ...data, tagline: e.target.value })} />
            </Field>
            <Field label="Copyright">
              <input className={inputCls} value={data.copyright} onChange={e => setData({ ...data, copyright: e.target.value })} />
            </Field>
            <Field label="Cor de Fundo do Footer">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-lg border border-gray-200 overflow-hidden shrink-0">
                  <div className="w-full h-full" style={{ background: data.bg_color }} />
                  <input type="color" value={data.bg_color} onChange={e => setData({ ...data, bg_color: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                </div>
                <input className={inputCls} value={data.bg_color} onChange={e => setData({ ...data, bg_color: e.target.value })} placeholder="#260a23" />
              </div>
            </Field>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Contato</h2>
            <Field label="E-mail"><input className={inputCls} value={data.email} onChange={e => setData({ ...data, email: e.target.value })} /></Field>
            <Field label="Telefone"><input className={inputCls} value={data.phone} onChange={e => setData({ ...data, phone: e.target.value })} /></Field>
            <Field label="Endereço"><input className={inputCls} value={data.address} onChange={e => setData({ ...data, address: e.target.value })} /></Field>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Redes Sociais</h2>
            <Field label="Instagram"><input className={inputCls} value={data.instagram} onChange={e => setData({ ...data, instagram: e.target.value })} placeholder="https://instagram.com/..." /></Field>
            <Field label="Facebook"><input className={inputCls} value={data.facebook} onChange={e => setData({ ...data, facebook: e.target.value })} placeholder="https://facebook.com/..." /></Field>
            <Field label="WhatsApp"><input className={inputCls} value={data.whatsapp} onChange={e => setData({ ...data, whatsapp: e.target.value })} placeholder="https://wa.me/55..." /></Field>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Links de Navegação</h2>
            {[1, 2, 3].map(n => (
              <div key={n} className="grid grid-cols-2 gap-3">
                <Field label={`Link ${n} — Texto`}>
                  <input className={inputCls} value={data[`link${n}_label` as keyof FooterData] as string} onChange={e => setData({ ...data, [`link${n}_label`]: e.target.value })} />
                </Field>
                <Field label={`Link ${n} — URL`}>
                  <input className={inputCls} value={data[`link${n}_url` as keyof FooterData] as string} onChange={e => setData({ ...data, [`link${n}_url`]: e.target.value })} />
                </Field>
              </div>
            ))}
          </div>

          {/* Preview */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3 mb-4">Pré-visualização</h2>
            <div className="rounded-lg overflow-hidden" style={{ background: data.bg_color }}>
              <div className="px-5 py-5 border-b border-white/10 text-center">
                <p className="text-amber-400/70 text-[9px] tracking-widest uppercase mb-1">Ma Gádeni</p>
                <p className="text-white/80 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{data.tagline}</p>
              </div>
              <div className="px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-4">
                  {[1, 2, 3].map(n => (
                    <span key={n} className="text-white/40 text-[9px] tracking-widest uppercase">
                      {data[`link${n}_label` as keyof FooterData] as string}
                    </span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <i className="ri-instagram-line text-white/30 text-sm"></i>
                  <i className="ri-facebook-line text-white/30 text-sm"></i>
                  <i className="ri-whatsapp-line text-white/30 text-sm"></i>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-white/5">
                <p className="text-white/20 text-[9px]">{data.copyright}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
